import React, { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Plus, Loader2, Trash2, Upload, Film, Users, FileText,
  FileSpreadsheet, X, Image as ImageIcon, Video, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import TestManagementPanel from '../../components/TestManagementPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VMCQModulePage = ({ embedded = false }) => {
  const [tests, setTests] = useState([]);
  const [classes, setClasses] = useState([]);
  // Sections removed - using classes directly
  const [loading, setLoading] = useState(true);
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  // Form state
  const [testName, setTestName] = useState('');
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [importClassId, setImportClassId] = useState('');
  const [saving, setSaving] = useState(false);

  // Media groups: [{id, file, uploadedMedia: {path, filename, media_type}, display_duration, question_count, questions_text, loading}]
  const [mediaGroups, setMediaGroups] = useState([]);

  const fileInputRef = useRef(null);
  const xlsxInputRef = useRef(null);
  const activeGroupRef = useRef(null);

  const getClassDisplayName = (cls) => (cls.name || '').replace(/^Class\s+/i, '');
  // Helper functions removed - using classes directly

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [testsRes, classesRes] = await Promise.all([
        api.get('/tests?module_type=vmcq'),
        api.get('/classes'),
        // // sections API removed - removed
      ]);
      setTests(testsRes.data);
      setClasses(classesRes.data);
      // setSections removed
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addMediaGroup = () => {
    setMediaGroups(prev => [...prev, {
      id: Date.now(),
      file: null,
      uploadedMedia: null,
      display_duration: 10,
      question_count: 1,
      questions_text: '',
      uploading: false,
      uploadProgress: 0,
      dragActive: false
    }]);
  };

  const removeMediaGroup = (idx) => {
    setMediaGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const updateGroup = (idx, key, value) => {
    setMediaGroups(prev => prev.map((g, i) => i === idx ? { ...g, [key]: value } : g));
  };

  const handleMediaUpload = async (file, idx) => {
    if (!file) return;
    updateGroup(idx, 'file', file);
    updateGroup(idx, 'uploading', true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          updateGroup(idx, 'uploadProgress', pct);
        }
      });
      updateGroup(idx, 'uploadedMedia', {
        path: res.data.video_path,
        filename: res.data.video_filename,
        media_type: res.data.media_type || 'video',
        original_filename: res.data.original_filename
      });
      toast.success('Media uploaded successfully');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      updateGroup(idx, 'uploading', false);
    }
  };

  const handleDrop = useCallback((e, idx) => {
    e.preventDefault();
    updateGroup(idx, 'dragActive', false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleMediaUpload(file, idx);
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    updateGroup(idx, 'dragActive', true);
  }, []);

  const parseQuestionsText = (text) => {
    const lines = text.trim().split('\n');
    const questions = [];
    for (const line of lines) {
      if (!line.trim()) continue;
      const parts = line.split('|').map(p => p.trim());
      if (parts.length < 6) continue;
      const [question_text, opt1, opt2, opt3, opt4, correctStr] = parts;
      const explanation = parts.length >= 7 ? parts[6] : null;
      const correct_option = parseInt(correctStr) - 1;
      if (correct_option < 0 || correct_option > 3) continue;
      questions.push({ question_text, options: [opt1, opt2, opt3, opt4], correct_option, explanation });
    }
    return questions;
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    if (!testName || selectedClassIds.length === 0) {
      toast.error('Please fill in the test name and select classes');
      return;
    }
    if (mediaGroups.length === 0) {
      toast.error('Please add at least one media group');
      return;
    }
    // Use first selected class if importClassId not specified
    const questionClassId = importClassId || selectedClassIds[0];

    setSaving(true);
    try {
      const allQuestionIds = [];
      const groupMeta = [];

      for (const group of mediaGroups) {
        if (!group.uploadedMedia) {
          toast.error('Please upload media for all groups');
          setSaving(false);
          return;
        }
        const parsed = parseQuestionsText(group.questions_text);
        if (parsed.length === 0) {
          toast.error('Please enter at least one question per media group');
          setSaving(false);
          return;
        }
        const qIds = [];
        for (const q of parsed) {
          const res = await api.post('/questions', {
            ...q,
            class_id: questionClassId,
            module_type: 'vmcq'
          });
          qIds.push(res.data.id || res.data);
        }
        allQuestionIds.push(...qIds);
        groupMeta.push({
          media_path: group.uploadedMedia.path,
          media_filename: group.uploadedMedia.filename,
          media_type: group.uploadedMedia.media_type,
          original_filename: group.uploadedMedia.original_filename,
          display_duration: group.uploadedMedia.media_type === 'image' ? (group.display_duration || 10) : null,
          question_count: qIds.length
        });
      }

      // using class_ids directly
      const firstGroup = groupMeta[0];

      const res = await api.post('/tests', {
        test_name: testName,
        module_type: 'vmcq',
        section_ids: selectedClassIds,  // Direct class assignment
        question_ids: allQuestionIds,
        video_path: firstGroup.media_path,
        video_filename: firstGroup.media_filename,
        media_groups: JSON.stringify(groupMeta)
      });

      toast.success(`${res.data.test_number} created with ${allQuestionIds.length} questions`);
      resetForm();
      setTestDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setTestName('');
    setSelectedClassIds([]);
    setImportClassId('');
    setMediaGroups([]);
  };

  const toggleClass = (classId) => {
    setSelectedClassIds(prev =>
      prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]
    );
  };

  const content = (
    <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Film className="h-6 w-6 text-purple-600" />
              Cinematic MCQ
            </h1>
            <p className="text-slate-500">Create video/image-based comprehension quizzes</p>
          </div>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => { resetForm(); setTestDialogOpen(true); }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Test
          </Button>
        </div>

        {/* Test Creation Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setTestDialogOpen(open); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Film className="h-5 w-5 text-purple-600" />
                Create Cinematic MCQ Test
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleCreateTest} className="space-y-6">
              {/* Test name */}
              <div className="space-y-2">
                <Label>Test Name *</Label>
                <Input
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="e.g., Understanding the Solar System"
                />
              </div>

              {/* Classes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Assign to Classes *</Label>
                  <button type="button" onClick={() => setSelectedClassIds(selectedClassIds.length === classes.length ? [] : classes.map(c => c.id))} className="text-xs text-purple-500 hover:text-purple-700 dark:text-purple-400 font-medium underline-offset-2 hover:underline">
                    {selectedClassIds.length === classes.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 border rounded-lg p-4 max-h-48 overflow-y-auto">
                  {classes.map(cls => {
                    const sel = selectedClassIds.includes(cls.id);
                    return (
                      <label key={cls.id} className={`flex items-center justify-center p-2 rounded-lg border-2 cursor-pointer text-sm font-semibold transition-all ${sel ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'}`}>
                        <input type="checkbox" className="sr-only" checked={sel} onChange={() => toggleClass(cls.id)} />
                        {getClassDisplayName(cls)}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Questions Class - HIDDEN (not required, auto-uses first selected class) */}
              <div className="hidden">
                <Label>Questions Class</Label>
                <Select value={importClassId} onValueChange={setImportClassId}>
                  <SelectTrigger><SelectValue placeholder="Select class for questions" /></SelectTrigger>
                  <SelectContent>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Media Groups */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Media Groups *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addMediaGroup} className="border-purple-300 text-purple-600 hover:bg-purple-50">
                    <Plus className="h-4 w-4 mr-1" /> Add Media Group
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Each media group (video or image) unlocks a set of questions for students.</p>

                {mediaGroups.length === 0 && (
                  <div className="border-2 border-dashed border-purple-200 dark:border-purple-800 rounded-xl p-6 text-center">
                    <Film className="h-10 w-10 mx-auto text-purple-300 mb-3" />
                    <p className="text-slate-500 text-sm">Click "Add Media Group" to start building your cinematic quiz</p>
                  </div>
                )}

                {mediaGroups.map((group, idx) => (
                  <Card key={group.id} className="border-2 border-purple-200 dark:border-purple-800">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                          Media Group {idx + 1}
                        </h4>
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeMediaGroup(idx)} className="text-rose-500 h-7 w-7 p-0">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Upload area */}
                      {!group.uploadedMedia ? (
                        <div
                          className={`border-2 border-dashed rounded-xl p-5 text-center transition-all ${group.dragActive ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-slate-300 dark:border-slate-700 hover:border-purple-400'}`}
                          onDrop={(e) => handleDrop(e, idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDragLeave={() => updateGroup(idx, 'dragActive', false)}
                        >
                          <div className="flex justify-center gap-4 mb-3">
                            <Film className="h-8 w-8 text-purple-300" />
                            <ImageIcon className="h-8 w-8 text-purple-300" />
                          </div>
                          <p className="text-sm text-slate-500 mb-2">Drag & drop a video or image</p>
                          <p className="text-xs text-slate-400 mb-3">Supports: MP4, WebM, MOV, AVI, PNG, JPEG, GIF, WebP</p>
                          {group.uploading ? (
                            <div className="space-y-2">
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div className="bg-purple-600 h-2 rounded-full transition-all" style={{ width: `${group.uploadProgress}%` }} />
                              </div>
                              <p className="text-xs text-purple-600">{group.uploadProgress}% uploaded...</p>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                className="hidden"
                                id={`media-${group.id}`}
                                accept="video/*,image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleMediaUpload(f, idx);
                                }}
                              />
                              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById(`media-${group.id}`)?.click()}>
                                <Upload className="h-4 w-4 mr-1" /> Browse
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                          {group.uploadedMedia.media_type === 'image'
                            ? <ImageIcon className="h-8 w-8 text-purple-600 shrink-0" />
                            : <Video className="h-8 w-8 text-purple-600 shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                              {group.uploadedMedia.original_filename}
                            </p>
                            <p className="text-xs text-purple-600 capitalize">{group.uploadedMedia.media_type} uploaded</p>
                          </div>
                          <Button type="button" size="sm" variant="ghost" onClick={() => updateGroup(idx, 'uploadedMedia', null)} className="text-rose-500">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}

                      {/* Image display duration */}
                      {group.uploadedMedia?.media_type === 'image' && (
                        <div className="flex items-center gap-3">
                          <Label className="text-sm shrink-0">Display Duration (seconds):</Label>
                          <Input
                            type="number"
                            min="3"
                            max="300"
                            value={group.display_duration}
                            onChange={(e) => updateGroup(idx, 'display_duration', parseInt(e.target.value) || 10)}
                            className="w-24"
                          />
                          <span className="text-xs text-slate-400">Default: 10s</span>
                        </div>
                      )}

                      {/* Questions for this group */}
                      <div className="space-y-2">
                        <Label className="text-sm">Questions for this media (paste format)</Label>
                        <Textarea
                          value={group.questions_text}
                          onChange={(e) => updateGroup(idx, 'questions_text', e.target.value)}
                          placeholder="Question|Option1|Option2|Option3|Option4|CorrectOption(1-4)|Explanation(Optional)&#10;What does the video show?|A cat|A dog|A bird|A fish|2&#10;What color is the sky?|Red|Blue|Green|Yellow|2|The sky appears blue due to scattering"
                          rows={5}
                          className="font-mono text-xs"
                        />
                        <p className="text-xs text-slate-500">
                          {parseQuestionsText(group.questions_text).length} question(s) in this group
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {mediaGroups.length > 0 && (
                  <div className="text-sm text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                    Total: {mediaGroups.reduce((sum, g) => sum + parseQuestionsText(g.questions_text).length, 0)} questions across {mediaGroups.length} media group(s)
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { resetForm(); setTestDialogOpen(false); }}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Cinematic Test
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tests List */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>
        ) : tests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Film className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No cinematic tests created yet</p>
            </CardContent>
          </Card>
        ) : (
          <TestManagementPanel
            tests={tests}
            classes={classes}
            onRefresh={fetchData}
            moduleColor="purple"
            moduleType="vmcq"
          />
        )}
      </div>
  );
  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
};

export default VMCQModulePage;
